<script setup lang="ts">
import MissionReward from '../items/MissionReward.vue';
import { useMissionCard } from '~/composables/useMissionCard';

const props = defineProps<{
  mission: TMissionOrBadge;
}>();

const emit = defineEmits<{
  missionOptedIn: [missionId: number];
}>();

const {
  mission,
  isRewardModalVisible,
  rewardData,
  buttonState,
  handleButtonClick,
  handleMissionClick,
} = useMissionCard(props, (missionId) => emit('missionOptedIn', missionId));
</script>

<template>
  <div class="mission-card">
    <!-- Header: Imagem + Info -->
    <div class="mission-card__header">
      <SmarticoApostatudoSmarticoImage
        :alt="mission.name"
        class="mission-card__image"
        :src="mission.image"
      />
      <div class="mission-card__info">
        <div
          class="mission-card__title"
          @click="handleMissionClick('reward')"
        >
          {{ mission.name }}
        </div>
        <div
          class="mission-card__description"
          @click="handleMissionClick('reward')"
        >
          {{ mission.description }}
        </div>
      </div>
    </div>

    <!-- Prêmio -->
    <div
      v-if="rewardData.reward"
      class="mission-card__reward"
      @click="handleMissionClick('reward')"
    >
      <div class="flex gap-1 items-center justify-center">
        <NuxtIcon
          class="mission-card__reward-icon"
          :filled="true"
          name="mission/gift"
        />
        <span class="mission-card__reward-label">PRÊMIO</span>
      </div>
      <span class="mission-card__reward-value">{{ rewardData.reward }}</span>
    </div>

    <!-- Botão -->
    <button
      v-if="buttonState"
      class="mission-card__button"
      :class="{
        'mission-card__button--participating':
          buttonState.text === 'Participando',
      }"
      :disabled="buttonState.disabled"
      @click.stop="handleButtonClick"
    >
      <span>{{ buttonState.text }}</span>
      <NuxtIcon
        v-if="buttonState.text !== 'Participando'"
        class="mission-card__button-icon"
        name="mission/button/participar"
      />
    </button>

    <!-- Modal de Reward -->
    <MissionReward
      v-model:modal-visible="isRewardModalVisible"
      :mission="mission"
    />
  </div>
</template>

<style scoped>
/* Card */
.mission-card {
  width: 253px;
  height: auto;
  background: var(--vipcard-bg);
  border: 1px solid var(--vipcard-border);
  border-radius: 10px;
  padding: 10px 5px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  user-select: none;
  box-sizing: border-box;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

/* Header: Imagem + Info */
.mission-card__header {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  height: 87px;
}

.mission-card__image {
  width: 87px;
  height: 87px;
  border-radius: 8px;
  flex-shrink: 0;
  object-fit: cover;
}

.mission-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  height: 87px;
}

.mission-card__title {
  font-size: 12px;
  font-weight: 700;
  color: white;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  cursor: pointer;
  line-height: 1.17;
}

.mission-card__title:hover {
  opacity: 0.8;
}

.mission-card__description {
  font-size: 10px;
  font-weight: 500;
  color: var(--vipcard-text-muted);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  cursor: pointer;
  line-height: 1.3;
}

.mission-card__description:hover {
  opacity: 0.8;
}

/* Prêmio */
.mission-card__reward {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 0;
  background: var(--vipcard-surface);
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
  width: 100%;
}

.mission-card__reward:hover {
  background: var(--vipcard-surface-hover);
}

.mission-card__reward-icon {
  font-size: 15px;
  color: var(--vipcard-accent);
  flex-shrink: 0;
}

.mission-card__reward-label {
  font-size: 10px;
  font-weight: 700;
  color: white;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.mission-card__reward-value {
  font-size: 10px;
  font-weight: 800;
  color: var(--vipcard-accent);
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  text-transform: uppercase;
}

/* Botão */
.mission-card__button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 30px;
  padding: 0 7px;
  background: var(--vipcard-btn-bg);
  border: none;
  border-radius: 4.5px;
  color: white;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  text-transform: uppercase;
  cursor: pointer;
  transition: filter 0.2s;
  flex-shrink: 0;
}

.mission-card__button-icon {
  font-size: 13px;
}

.mission-card__button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mission-card__button:hover:not(:disabled) {
  filter: brightness(1.1);
}

.mission-card__button--participating {
  background: rgba(23, 201, 100, 0.1);
  border: 1px solid rgba(23, 201, 100, 0.5);
  color: #17c964;
}
</style>
